/**
 * Domain Session Manager
 * Maneja la persistencia de sesión entre dominios/subdominios usando Shopify Multipass
 * 
 */

(function() {
  'use strict';

  const MULTIPASS_ENDPOINT = 'https://carrierservice.onrender.com/api/auth/multipass/cross-domain';
  const REQUEST_TIMEOUT = 5000; // 5 segundos

  /**
   * Valida formato de email
   */
  function isValidEmail(email) {
    if (!email || typeof email !== 'string') return false;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email.trim());
  }

  /**
   * Extrae el dominio de una URL
   */
  function extractDomain(url) {
    try {
      const urlObj = new URL(url);
      return urlObj.origin;
    } catch (e) {
      console.error('Invalid URL:', url, e);
      return null;
    }
  }

  /**
   * Extrae el path de retorno de una URL
   */
  function extractReturnPath(url) {
    try {
      const urlObj = new URL(url);
      return urlObj.pathname + urlObj.search + urlObj.hash;
    } catch (e) {
      return '/';
    }
  }

  /**
   * Realiza la llamada al endpoint de Multipass con timeout
   */
  function fetchWithTimeout(url, options, timeout = REQUEST_TIMEOUT) {
    return Promise.race([
      fetch(url, options),
      new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Request timeout')), timeout)
      )
    ]);
  }

  /**
   * Maneja la navegación cross-domain con persistencia de sesión
   * 
   * @param {string} targetUrl - URL de destino completa
   * @param {Object} customerData - Datos del cliente {email, first_name, last_name, shop_domain}
   * @returns {Promise<void>}
   */
  async function handleCrossDomainNavigation(targetUrl, customerData) {
    const { email, first_name, last_name, shop_domain } = customerData;

    // Validar que el usuario esté logueado
    if (!email || !isValidEmail(email)) {
      window.location.href = targetUrl;
      return;
    }

    const sourceDomain = window.location.origin;
    const targetDomain = extractDomain(targetUrl);

    // Si no se puede extraer el dominio o es el mismo, navegación estándar
    if (!targetDomain || sourceDomain === targetDomain) {
      window.location.href = targetUrl;
      return;
    }

    const returnTo = extractReturnPath(targetUrl);

    try {
      // Preparar payload para Multipass
      const payload = {
        email: email.trim(),
        source_domain: sourceDomain,
        target_domain: targetDomain,
        return_to: returnTo,
        shop_domain: shop_domain
      };

      // Añadir datos opcionales si existen
      if (first_name) payload.first_name = first_name;
      if (last_name) payload.last_name = last_name;

      // Llamar al endpoint de Multipass
      const response = await fetchWithTimeout(MULTIPASS_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload),
        mode: 'cors'
      });

      if (!response.ok) {
        throw new Error(`Multipass API error: ${response.status}`);
      }

      const data = await response.json();
      console.log(data)

      if (data.success && data.data && data.data.login_url) {
        window.location.href = data.data.login_url;
      } else {
        throw new Error('Invalid Multipass response');
      }

    } catch (error) {
      console.error('Cross-domain session transfer failed:', error);
      console.log('Falling back to standard navigation');
      // Fallback: navegación estándar sin sesión
      window.location.href = targetUrl;
    }
  }

  /**
   * Añade event listeners a enlaces cross-domain
   * 
   * @param {string} selector - Selector CSS para los enlaces
   */
  function attachCrossDomainHandlers(selector) {
    const links = document.querySelectorAll(selector);

    links.forEach(link => {
      link.addEventListener('click', function(e) {
        console.log(e)
        e.preventDefault();

        const targetUrl = this.href;
        const isProcessing = this.getAttribute('data-processing') === 'true';

        // Prevenir múltiples clicks
        if (isProcessing) {
          return;
        }

        // Marcar como procesando
        this.setAttribute('data-processing', 'true');
        this.style.opacity = '0.6';
        this.style.pointerEvents = 'none';

        // Extraer datos del cliente desde data attributes
        const customerData = {
          email: this.getAttribute('data-customer-email') || '',
          first_name: this.getAttribute('data-customer-firstname') || '',
          last_name: this.getAttribute('data-customer-lastname') || '',
          shop_domain: this.getAttribute('data-shop-domain') || ''
        };

        // Manejar la navegación
        handleCrossDomainNavigation(targetUrl, customerData)
          .catch(error => {
            console.error('Navigation error:', error);
            // En caso de error, restaurar el estado del enlace
            this.setAttribute('data-processing', 'false');
            this.style.opacity = '1';
            this.style.pointerEvents = 'auto';
          });
      });
    });
  }

  // Exponer funciones globalmente
  window.DomainSessionManager = {
    handleCrossDomainNavigation,
    attachCrossDomainHandlers
  };

  // Auto-inicializar cuando el DOM esté listo
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() {
      attachCrossDomainHandlers('.js-cross-domain-nav');
    });
  } else {
    // DOM ya está listo
    attachCrossDomainHandlers('.js-cross-domain-nav');
  }

})();
